// ============================================================================
// AFB-0009 — AM bounce-back notification.
//
// When finance/exec sends a Project Work card back to the AM (stage =
// Awaiting_AM_Input), the responsible AM should be told. Email is explicitly
// out of scope this round (separate Cloud Function work) — this is the in-app
// signal: a toast surfaced the next time the AM opens Project Work.
//
// We hook into the existing Toast surface (src/shared/components/Toast.tsx)
// rather than inventing a new notification store. Detection mirrors the
// inbox-count prior art (use-inbox-counts.ts): a single scoped query on mount.
//
// Scope/timing note (documented deviation): the prompt suggested firing on app
// *load*. Mounting a global listener would require touching the app shell,
// which is out of this round's scope (changes confined to project-work). So
// the toast fires when the AM lands on the Project Work area instead — the
// place they act on these cards. A sessionStorage guard keeps it to once per
// browser session so navigation within the module doesn't re-toast.
// ============================================================================

import { useEffect, useRef } from 'react';
import { supabase } from '../../../shared/supabase';
import { useToast } from '../../../shared/components';

const SESSION_GUARD_PREFIX = 'tcs:project-work:awaiting-toast-seen:';

interface AwaitingRow {
  project_id: string;
  account_name: string | null;
  facility_name: string | null;
  awaiting_am_input_at: string | null;
  facilities: { account_manager_eid: string | null } | null;
}

/**
 * Fires a one-per-session info toast if the signed-in AM has any project cards
 * bounced back to them (stage = Awaiting_AM_Input) since their last login.
 *
 * @param myEid        the viewer's employee_id (AM scoping key); null = no-op
 * @param lastLoginAt  profile.last_login_at — only notify for bounces newer
 *                     than this; null/absent means notify for any awaiting card
 */
export function useAwaitingAmToast(
  myEid: string | null | undefined,
  lastLoginAt: string | null | undefined,
): void {
  const { info } = useToast();
  // Guard against double-fire in React 18 StrictMode dev double-invoke.
  const firedRef = useRef(false);

  useEffect(() => {
    if (!myEid) return;
    if (firedRef.current) return;

    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase
          .from('projects')
          .select(
            'project_id, account_name, facility_name, awaiting_am_input_at, facilities!inner(account_manager_eid)',
          )
          .eq('stage', 'Awaiting_AM_Input')
          .eq('facilities.account_manager_eid', myEid);

        if (cancelled || error || !data) return;

        const rows = data as unknown as AwaitingRow[];
        // Only surface bounces that happened since the AM last logged in.
        const sinceCutoff = lastLoginAt ? new Date(lastLoginAt).getTime() : null;
        const relevant = rows.filter((r) => {
          if (sinceCutoff == null) return true;
          if (!r.awaiting_am_input_at) return true;
          return new Date(r.awaiting_am_input_at).getTime() > sinceCutoff;
        });

        if (relevant.length === 0) return;

        // Once-per-session guard, keyed by eid so a shared machine doesn't
        // suppress a different AM's toast within the same tab session.
        const guardKey = `${SESSION_GUARD_PREFIX}${myEid}`;
        try {
          if (window.sessionStorage.getItem(guardKey)) return;
          window.sessionStorage.setItem(guardKey, '1');
        } catch {
          /* sessionStorage unavailable — fall through and toast anyway */
        }

        firedRef.current = true;
        const first = relevant[0];
        const label = first.account_name ?? first.facility_name ?? 'A project';
        info(
          relevant.length === 1
            ? `${label} is awaiting your input.`
            : `${relevant.length} projects are awaiting your input.`,
          {
            description:
              relevant.length === 1
                ? 'Finance bounced this card back — open it to add the missing details.'
                // ⛔ 2026-09-03: this said 'filter by “Awaiting AM Input”' and
                // that chip no longer exists — #454 gave Draft and
                // Awaiting_AM_Input one label, "Not sent". Caught by the LIVE
                // BUNDLE sweep (TCSD-0035/0057), not by tsc or any test:
                // it is prose, and prose can name a control that is gone.
                : 'Finance bounced these cards back — they are under “Not sent”.',
            duration: 8000,
          },
        );
      } catch {
        // Non-fatal — a missed toast must never break the list page.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [myEid, lastLoginAt, info]);
}
